/**
 * 版面：把 960x600 的画面与界面分别缩放到舞台里。
 *
 * - #view（画面层）按 contain 缩放，任何屏幕比例都不变形；
 * - #ui（界面层）用同一套 960x600 内部坐标，缩放倍数尽量大但不越出可用区域，
 *   界面高度不够时截掉下面那截（面板居中依旧准确）。
 *
 * 这样手机上按钮、面板与电脑的相对位置和观感一致，不会出现「UI 跑出画面外」。
 */

const VW = 960;
const VH = 600;
const UI_MIN = 0.62;   // 界面最小缩放（再小就看不清了）
const UI_MAX = 1.3;    // 界面最大缩放（大屏上别撑太夸张）

/**
 * 刘海 / 圆角 / 底部手势条的实际像素。
 * 优先读 :root 上那四个变量（认得 env() 的浏览器会算出具体数值）；
 * 老浏览器里变量还是 env(...) 原文，量不出来，就用一个隐藏探针把真实像素读出来。
 */
function readSafeArea() {
  const cs = getComputedStyle(document.documentElement);
  const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : NaN;
  };
  const t = num(cs.getPropertyValue('--sat'));
  const r = num(cs.getPropertyValue('--sar'));
  const b = num(cs.getPropertyValue('--sab'));
  const l = num(cs.getPropertyValue('--sal'));
  if (Number.isFinite(t + r + b + l)) return { t, r, b, l };

  let probe = document.getElementById('safe-probe');
  if (!probe) {
    probe = document.createElement('div');
    probe.id = 'safe-probe';
    probe.style.cssText = 'position:fixed;left:0;top:0;width:0;height:0;visibility:hidden;'
      + 'pointer-events:none;padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) '
      + 'env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px);';
    document.body.appendChild(probe);
  }
  const ps = getComputedStyle(probe);
  return {
    t: parseFloat(ps.paddingTop) || 0,
    r: parseFloat(ps.paddingRight) || 0,
    b: parseFloat(ps.paddingBottom) || 0,
    l: parseFloat(ps.paddingLeft) || 0,
  };
}

export function installLayout() {
  const stage = document.getElementById('stage');
  const view = document.getElementById('view');
  const ui = document.getElementById('ui');
  if (!stage || !view || !ui) return () => {};

  const apply = () => {
    const cs = getComputedStyle(stage);
    const safe = readSafeArea();
    const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    const padY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const w = Math.max(1, stage.clientWidth - padX);   // 可用区（已让出刘海安全区）
    const h = Math.max(1, stage.clientHeight - padY);

    // 画面：完整装进可用区
    const s = Math.min(w / VW, h / VH);
    view.style.transform = `translate(-50%, -50%) scale(${s})`;

    // 界面：以宽度为准，字号在手机上不会缩到看不清
    let uiScale = Math.min(w / VW, UI_MAX);
    if (uiScale < UI_MIN) uiScale = UI_MIN;
    // 竖屏这类窄屏：界面层的内部尺寸随之收窄（内容自己重排），保证不越出屏幕
    let uiW = Math.max(320, Math.min(VW, w / uiScale));
    let uiH = Math.max(220, Math.min(VH, h / uiScale));
    // 收窄到极限仍装不下（极窄 / 极矮的屏）：再缩一点，绝不顶出可用区与安全区
    uiScale = Math.min(uiScale, w / uiW, h / uiH);

    ui.style.width = `${uiW}px`;
    ui.style.height = `${uiH}px`;
    ui.style.transform = `translate(-50%, -50%) scale(${uiScale})`;
    // 面板按这套内部坐标算自己能长多大（见 index.html 中 --ui-h 的用法）
    ui.style.setProperty('--ui-scale', String(uiScale));
    ui.style.setProperty('--ui-w', `${uiW}px`);
    // 窗口不许顶进刘海与底部手势条：可用高度先扣掉上下安全区（换算回内部坐标）
    ui.style.setProperty('--ui-h', `${Math.max(160, uiH - (safe.t + safe.b) / uiScale)}px`);
    // 顶栏、状态条、提示要躲开刘海：把安全区按界面缩放倍数换成内部坐标
    ui.style.setProperty('--sat-ui', `${safe.t / uiScale}px`);
    ui.style.setProperty('--sar-ui', `${safe.r / uiScale}px`);
    ui.style.setProperty('--sab-ui', `${safe.b / uiScale}px`);
    ui.style.setProperty('--sal-ui', `${safe.l / uiScale}px`);

    // 触屏控件（摇杆 / 动作按钮）要贴在画面区域内部：给出舞台内容区矩形，
    // 桌面上也不会跑到舞台外的背景里；手机上这就是整屏，摇杆与按钮再各自按
    // --sal / --sab 让开刘海与手势条（见 index.html）
    if (document.body) {
      const r = stage.getBoundingClientRect();
      const body = document.body.style;
      body.setProperty('--stage-x', `${r.left + stage.clientLeft + (parseFloat(cs.paddingLeft) || 0)}px`);
      body.setProperty('--stage-y', `${r.top + stage.clientTop + (parseFloat(cs.paddingTop) || 0)}px`);
      body.setProperty('--stage-w', `${w}px`);
      body.setProperty('--stage-h', `${h}px`);
    }
  };

  apply();
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(apply).observe(stage);
  }
  window.addEventListener('resize', apply);
  window.addEventListener('orientationchange', () => setTimeout(apply, 150));
  return apply;
}
